import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  CreateRegionPayload,
  RegionListQueryParams,
  RegionResponse,
  UpdateRegionPayload,
} from "@/types/regions.types";
import { UserResponse } from "@/types/user-management.types";

export const RegionManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRegions: builder.query<
      PaginatedApiResponse<RegionResponse[]>,
      RegionListQueryParams & { language?: string }
    >({
      query: (params) => ({
        url: "regions",
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
          language: params.language,
          orphaned: params.orphaned,
        },
      }),
      providesTags: ["RegionManagement"],
      keepUnusedDataFor: 0,
    }),
    createRegion: builder.mutation<
      ApiResponse<RegionResponse>,
      CreateRegionPayload
    >({
      query: (region) => ({
        url: "regions",
        method: "POST",
        data: region,
        headers: { "x-lang": "en" },
      }),
      invalidatesTags: [{ type: "RegionManagement", id: "LIST" }],
    }),
    getRegionById: builder.query<RegionResponse, string>({
      query: (region_id) => ({
        url: `/regions/${region_id}`,
        method: "GET",
        headers: { "x-lang": "en" },
      }),
      keepUnusedDataFor: 0,
    }),
    updateRegion: builder.mutation<
      ApiResponse<RegionResponse>,
      { region_id: string; data: UpdateRegionPayload }
    >({
      query: ({ region_id, data }) => ({
        url: `/regions/${region_id}`,
        method: "PATCH",
        data,
        headers: { "x-lang": "en" },
      }),
      invalidatesTags: (result, error, { region_id }) => [
        { type: "RegionManagement", id: region_id },
        { type: "RegionManagement", id: "LIST" },
      ],
    }),
    deleteRegion: builder.mutation<ApiResponse<null>, string>({
      query: (region_id) => ({
        url: `/regions/${region_id}`,
        method: "DELETE",
        headers: { "x-lang": "en" },
      }),
      invalidatesTags: [{ type: "RegionManagement", id: "LIST" }],
    }),
    getOperatorCandidates: builder.query<
      PaginatedApiResponse<UserResponse>,
      { page?: number; limit?: number; lang?: "en" | "fi" | "de" | "sv" }
    >({
      query: ({ page, limit, lang = "en" }) => ({
        url: "regions/operator-candidates",
        method: "GET",
        params: { page, limit },
        headers: { "x-lang": lang },
      }),
    }),
    getDmoCandidates: builder.query<
      PaginatedApiResponse<UserResponse>,
      { page?: number; limit?: number; lang?: "en" | "fi" | "de" | "sv" }
    >({
      query: ({ page, limit, lang = "en" }) => ({
        url: "regions/dmo-candidates",
        method: "GET",
        params: { page, limit },
        headers: { "x-lang": lang },
      }),
    }),
  }),
});

export const {
  useGetRegionsQuery,
  useCreateRegionMutation,
  useGetRegionByIdQuery,
  useUpdateRegionMutation,
  useDeleteRegionMutation,
  useGetOperatorCandidatesQuery,
  useGetDmoCandidatesQuery,
} = RegionManagementService;
